/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Form,
  FloatingLabel,
  OverlayTrigger,
  Popover,
  Button
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsDownToLine } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'components/base/DatePicker';
import {
  fetchHVARiskCategories,
  fetchHVARiskElements,
  HVARiskCategory,
  HVARiskElement
} from 'services/hvarisk.service';
import {
  fetchHVACampaigns,
  fetchHVAServiceNames,
  HVACampaign,
  HVAServiceName
} from 'services/hvaservicecampaign.service';

interface RiskFilterBarProps {
  filters: any;
  onFilterChange: (filters: any) => void;
  target?: 'review' | 'dashboard';
}

const RiskFilterBar: React.FC<RiskFilterBarProps> = ({
  filters,
  onFilterChange,
  target = 'review'
}) => {
  const [campaigns, setCampaigns] = useState<HVACampaign[]>([]);
  const [services, setServices] = useState<HVAServiceName[]>([]);
  const [categories, setCategories] = useState<HVARiskCategory[]>([]);
  const [riskElements, setRiskElements] = useState<HVARiskElement[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);
  const [dateRangeOption, setDateRangeOption] = useState(
    target === 'dashboard' ? 'last30days' : 'today'
  );
  const [showPopover, setShowPopover] = useState(false);
  const [shouldBounce, setShouldBounce] = useState(false);

  // Estado temporal para los filtros antes de aplicar
  const [tempFilters, setTempFilters] = useState({
    serviceId: filters.serviceId || '0',
    campaignId: filters.campaignId || '0',
    categoryId: filters.categoryId || '0',
    riskElementId: filters.riskElementId || '0',
    severity: filters.severity || '0',
    status: filters.status || 'pending'
  });

  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [servicesData, campaignsData, categoriesData, elementsData] =
          await Promise.all([
            fetchHVAServiceNames(),
            fetchHVACampaigns(),
            fetchHVARiskCategories(),
            fetchHVARiskElements()
          ]);
        setServices(servicesData);
        setCampaigns(campaignsData);
        setCategories(categoriesData);
        setRiskElements(elementsData);
      } catch (error) {
        console.error('Error loading filter data:', error);
      }
    };

    loadFilterData();

    // Establecer rango de fechas inicial según el target
    if (target === 'dashboard') {
      handleDateRangeOptionChange('last30days', true);
    } else {
      const today = new Date();
      setSelectedDateRange([today, today]);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.target;
    setTempFilters(prevState => ({
      ...prevState,
      [id]: value
    }));
    setShouldBounce(true);
  };

  const handleDateChange = (date: Date[]) => {
    if (date.length === 2) {
      setSelectedDateRange(date);
      setShowPopover(false);
      setShouldBounce(true);
    }
  };

  const handleDateRangeOptionChange = (
    value: string,
    isInitialLoad = false
  ) => {
    setDateRangeOption(value);
    let startDate: Date, endDate: Date;
    const today = new Date();

    // Diferentes opciones según el target
    if (target === 'dashboard') {
      switch (value) {
        case 'last7days':
          startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          endDate = today;
          break;
        case 'last30days':
          startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          endDate = today;
          break;
        case 'last90days':
          startDate = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
          endDate = today;
          break;
        case 'thisyear':
          startDate = new Date(today.getFullYear(), 0, 1);
          endDate = today;
          break;
        case 'custom':
          return;
        default:
          startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          endDate = today;
      }
    } else {
      const yesterday = new Date();
      // Opciones para review
      switch (value) {
        case 'today':
          startDate = new Date(today.setHours(0, 0, 0, 0));
          endDate = new Date();
          break;
        case 'yesterday':
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = new Date(yesterday.setHours(0, 0, 0, 0));
          endDate = new Date(yesterday.setHours(23, 59, 59, 999));
          break;
        case 'last7days':
          startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          endDate = new Date();
          break;
        case 'last30days':
          startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          endDate = new Date();
          break;
        case 'custom':
          return;
        default:
          return;
      }
    }

    setSelectedDateRange([startDate, endDate]);
    setShowPopover(false);
    // Solo activar bounce si NO es la carga inicial
    if (!isInitialLoad) {
      setShouldBounce(true);
    }
  };

  const formatDateRange = (dates: Date[]) => {
    if (dates.length === 2) {
      const start = dates[0];
      const end = dates[1];

      const formatOptions: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      };

      const startStr = start.toLocaleDateString('en-US', formatOptions);
      const endStr = end.toLocaleDateString('en-US', formatOptions);

      // Si es el mismo día
      if (start.toDateString() === end.toDateString()) {
        return startStr;
      }

      // Si es el mismo año, no repetir el año en la fecha inicial
      if (start.getFullYear() === end.getFullYear()) {
        const startWithoutYear = start.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        return `${startWithoutYear} - ${endStr}`;
      }

      return `${startStr} - ${endStr}`;
    }
    return '';
  };

  const applyFilters = () => {
    onFilterChange({
      serviceId:
        tempFilters.serviceId === '0' ? null : Number(tempFilters.serviceId),
      campaignId:
        tempFilters.campaignId === '0' ? null : Number(tempFilters.campaignId),
      categoryId:
        tempFilters.categoryId === '0' ? null : Number(tempFilters.categoryId),
      riskElementId:
        tempFilters.riskElementId === '0'
          ? null
          : Number(tempFilters.riskElementId),
      severity:
        tempFilters.severity === '0' ? null : Number(tempFilters.severity),
      status: tempFilters.status,
      dateRange: 'custom',
      startDate: selectedDateRange[0],
      endDate: selectedDateRange[1]
    });
    setShouldBounce(false);
  };

  // Diferentes opciones de fecha según el target
  const dateOptions =
    target === 'dashboard'
      ? ['last7days', 'last30days', 'last90days', 'thisyear', 'custom']
      : ['today', 'yesterday', 'last7days', 'last30days', 'custom'];

  const dateOptionLabels: Record<string, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    last7days: 'Last 7 days',
    last30days: 'Last 30 days',
    last90days: 'Last 90 days',
    thisyear: 'This year',
    custom: 'Custom range'
  };

  const datePopover = (
    <Popover id="date-range-popover" className="shadow-lg lh-sm fs-9">
      <Popover.Body>
        <p>Select a date range option:</p>
        <div className="mb-3">
          {dateOptions.map(option => (
            <div key={option} className="form-check form-switch mb-0">
              <label className="d-flex fs-9 justify-content-start align-items-center mx-2">
                <input
                  type="checkbox"
                  value={option}
                  checked={dateRangeOption === option}
                  onChange={() => handleDateRangeOptionChange(option)}
                  className="form-check-input pt-0"
                />
                <div className="fs-9 fw-semibold pt-1 px-2">
                  {dateOptionLabels[option]}
                </div>
              </label>
            </div>
          ))}
        </div>
        {dateRangeOption === 'custom' && (
          <>
            <p>Select a custom date range from the date picker below:</p>
            <DatePicker
              value={selectedDateRange}
              onChange={handleDateChange}
              options={{
                dateFormat: 'M j, Y',
                mode: 'range',
                onClose: () => setShowPopover(false)
              }}
            />
          </>
        )}
      </Popover.Body>
    </Popover>
  );

  // Filtrar elementos de riesgo según la categoría seleccionada
  const filteredRiskElements =
    tempFilters.categoryId !== '0'
      ? riskElements.filter(
          element => element.categoryid === Number(tempFilters.categoryId)
        )
      : riskElements;

  // Clase de fondo según el target
  const inputClass = target === 'dashboard' ? 'bg-body-emphasis' : 'bg-body';

  return (
    <div className="py-0 px-0">
      <div className="text-start px-0 pb-0">
        <label className="form-label px-2 text-danger fw-semibold">
          {target === 'dashboard'
            ? 'Risk Review Filters'
            : 'Risk Review Filters'}
        </label>
      </div>
      <div className="px-0 pe-2 d-flex flex-wrap justify-content-start align-items-center">
        <div className="p-0 me-1">
          <FloatingLabel controlId="dateRange" label="Date Range">
            <OverlayTrigger
              trigger="click"
              placement={target === 'dashboard' ? 'bottom' : 'top'}
              overlay={datePopover}
              rootClose={false}
              show={showPopover}
              onToggle={nextShow => setShowPopover(nextShow)}
            >
              <Form.Control
                type="text"
                value={formatDateRange(selectedDateRange)}
                className={inputClass}
                readOnly
                style={{ width: '185px', cursor: 'pointer' }}
              />
            </OverlayTrigger>
          </FloatingLabel>
        </div>

        <div className="p-0 me-1">
          <FloatingLabel controlId="serviceId" label="Service">
            <Form.Select
              id="serviceId"
              value={tempFilters.serviceId}
              onChange={handleInputChange}
              style={{ width: '180px' }}
              className={inputClass}
            >
              <option value="0">Any service</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </div>

        <div className="p-0 me-1">
          <FloatingLabel controlId="campaignId" label="Campaign">
            <Form.Select
              id="campaignId"
              value={tempFilters.campaignId}
              onChange={handleInputChange}
              style={{ width: '200px' }}
              className={inputClass}
            >
              <option value="0">Any campaign</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </div>

        <div className="p-0 me-1">
          <FloatingLabel controlId="categoryId" label="Risk Category">
            <Form.Select
              id="categoryId"
              value={tempFilters.categoryId}
              onChange={handleInputChange}
              style={{ width: '180px' }}
              className={inputClass}
            >
              <option value="0">Any category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </div>

        <div className="p-0 me-1">
          <FloatingLabel controlId="riskElementId" label="Risk Element">
            <Form.Select
              id="riskElementId"
              value={tempFilters.riskElementId}
              onChange={handleInputChange}
              style={{ width: '200px' }}
              className={inputClass}
              disabled={filteredRiskElements.length === 0}
            >
              <option value="0">Any element</option>
              {filteredRiskElements.map(element => (
                <option key={element.id} value={element.id}>
                  {element.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </div>

        <div className="p-0 me-1">
          <FloatingLabel controlId="severity" label="Severity">
            <Form.Select
              id="severity"
              value={tempFilters.severity}
              onChange={handleInputChange}
              style={{ width: '140px' }}
              className={inputClass}
            >
              <option value="0">Any severity</option>
              <option value="5">Critical (5)</option>
              <option value="4">High (4)</option>
              <option value="3">Medium (3)</option>
              <option value="2">Low (2)</option>
              <option value="1">Very Low (1)</option>
            </Form.Select>
          </FloatingLabel>
        </div>

        <div className="d-flex justify-content-start align-items-center">
          <Button
            variant="link"
            onClick={applyFilters}
            className="px-1 pt-4"
            title="Apply filters"
          >
            <FontAwesomeIcon
              icon={faArrowsDownToLine}
              size="xl"
              bounce={shouldBounce}
              className="text-danger"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RiskFilterBar;
